import { stripHtmlTags } from '../utils/html';
import type CurrencyFormatter from '../../tnew/currency/CurrencyFormatter';
import {
  FeeDisclaimers, GPDPricing, GPDScreenZone,
  ExtendedPriceTypeData, Zone,
  SyosInitDataResponse,
} from './types';
import ZonePriceTypeMap from './ZonePriceTypeMap';
import getPriceRangeForPriceTypes from './getPriceRangeForPriceTypes';

function getUniqueArray<T>(value: Array<T>) {
  return Array.from(new Set(value).values());
}

function getPriceTypeSelectionButtonAccessibleName(description: string, displayPrice: string, feeDisclaimer: string | null) {
  let value = `${description}, ${displayPrice}`;

  if (feeDisclaimer) {
    const feeDisclaimerPlainText = stripHtmlTags(feeDisclaimer);
    value += ` (${feeDisclaimerPlainText})`;
  }

  return value;
}

function massagePriceTypeData(data: GPDPricing, feeDisclaimers: FeeDisclaimers, isFeeDisclaimerVisible: boolean, currencyFormatter: CurrencyFormatter): ExtendedPriceTypeData {
  const { PerSeatFee } = data;
  let feeDisclaimer = null;

  if (isFeeDisclaimerVisible && PerSeatFee !== 0) {
    const key = `${data.price_type}-${data.zone_no}`;
    feeDisclaimer = feeDisclaimers[key];
  }

  const priceWithFee = data.price + PerSeatFee;
  const displayPrice = currencyFormatter.format(priceWithFee);

  const priceTypeSelectionButtonAccessibleName = getPriceTypeSelectionButtonAccessibleName(data.price_type_desc, displayPrice, feeDisclaimer);

  return {
    ...data,
    PerSeatFee,
    feeDisclaimer,
    priceTypeSelectionButtonAccessibleName,
    priceWithFee,
    displayPrice,
  };
}

function sortPriceTypes(priceTypes: ExtendedPriceTypeData[], sortOrder: string) {
  const isDescending = sortOrder.endsWith('Descending');
  const sortMode = sortOrder.replace(/(Ascending|Descending)$/, '');

  let propertyName: 'price_type' | 'price_type_desc' | 'priceWithFee';

  switch (sortMode) {
    case 'PriceType':
      propertyName = 'price_type';
      break;
    case 'PriceTypeDescription':
      propertyName = 'price_type_desc';
      break;
    default:
      propertyName = 'priceWithFee';
      break;
  }

  const multiplier = isDescending ? -1 : 1;

  priceTypes.sort((priceTypeA, priceTypeB) => {
    const a = priceTypeA[propertyName];
    const b = priceTypeB[propertyName];

    if (a > b) {
      return 1 * multiplier;
    }

    if (a < b) {
      return -1 * multiplier;
    }

    return 0;
  });
}

function getPriceRangeIncludesAllPriceTypes(allZones: GPDScreenZone[], defaultPriceTypeId: number | undefined, zonePriceTypeMap: ZonePriceTypeMap, globalPriceRangeIncludesAllPriceTypes: boolean) {
  if (globalPriceRangeIncludesAllPriceTypes) {
    return true;
  }

  if (!defaultPriceTypeId) {
    return true;
  }

  const uniqueZoneIds = getUniqueArray(allZones.map((zone) => zone.zone_no));

  const allZonesContainDefaultPriceType = uniqueZoneIds.every((zoneId) => zonePriceTypeMap.getPriceType(zoneId, defaultPriceTypeId));

  if (allZonesContainDefaultPriceType) {
    return false;
  }

  return true;
}

function buildZonesMap(screenZoneList: GPDScreenZone[], zonePriceTypeMap: ZonePriceTypeMap, priceRangeIncludesAllPriceTypes: boolean, defaultPriceTypeId: number, currencyFormatter: CurrencyFormatter, priceRangeSeparator: string) {
  const zones = screenZoneList
    .map((screenZoneData) => {
      const zoneId = screenZoneData.zone_no;

      const priceTypes = zonePriceTypeMap.getZonePriceTypes(zoneId);

      if (priceTypes.length === 0) {
        return undefined;
      }

      let priceRange;

      if (priceRangeIncludesAllPriceTypes) {
        priceRange = getPriceRangeForPriceTypes(priceTypes);
      } else {
        priceRange = [zonePriceTypeMap.getPriceType(zoneId, defaultPriceTypeId).priceWithFee];
      }

      const formattedPrices = priceRange.map((price) => currencyFormatter.format(price));

      let accessiblePriceRange: string;

      if (formattedPrices.length === 1) {
        accessiblePriceRange = formattedPrices[0];
      } else {
        accessiblePriceRange = `${formattedPrices[0]} ${priceRangeSeparator} ${formattedPrices[formattedPrices.length - 1]}`;
      }

      const zone: Zone = {
        id: zoneId,
        label: priceTypes[0].description,
        priceTypes,
        priceRange,
        formattedPrices,
        accessiblePriceRange,
        color: '#CCC',
        iconCharacter: '',
      };

      return zone;
    });

  return new Map(
    zones
      .filter((zone) => !!zone)
      .map((zone) => [zone.id, zone]),
  );
}

export interface ProcessPerformanceDetailsData {
  initDataResponse: SyosInitDataResponse;
  currencyFormatter: CurrencyFormatter;
  feeDisclaimers: FeeDisclaimers;
  isFeeDisclaimerVisible: boolean;
  priceRangeSeparator: string;
  globalPriceRangeIncludesAllPriceTypes: boolean;
  priceTypeSortOrder: string
}

function processPerformanceDetails({
  initDataResponse,
  currencyFormatter,
  feeDisclaimers,
  isFeeDisclaimerVisible,
  priceRangeSeparator,
  globalPriceRangeIncludesAllPriceTypes,
  priceTypeSortOrder,
}: ProcessPerformanceDetailsData) {
  const screenZonesMap = initDataResponse.ScreenZoneList.map((o) => ({
    screenId: o.screen_no,
    zoneId: o.zone_no,
  }));

  // Remove unavailable zones from data set
  const screenZoneList = initDataResponse.ScreenZoneList.filter((zone) => zone.zone_no !== 0);

  const pricing = initDataResponse.Pricing.map((priceType) => massagePriceTypeData(priceType, feeDisclaimers, isFeeDisclaimerVisible, currencyFormatter));

  sortPriceTypes(pricing, priceTypeSortOrder);

  const zonePriceTypeMap = new ZonePriceTypeMap(pricing);

  const defaultPriceType = pricing
    .find((el) => el.is_default);

  let defaultPriceTypeId: number | undefined;

  if (defaultPriceType) {
    defaultPriceTypeId = defaultPriceType.price_type;
  }

  const priceRangeIncludesAllPriceTypes = getPriceRangeIncludesAllPriceTypes(screenZoneList, defaultPriceTypeId, zonePriceTypeMap, globalPriceRangeIncludesAllPriceTypes);

  const zones = buildZonesMap(screenZoneList, zonePriceTypeMap, priceRangeIncludesAllPriceTypes, defaultPriceTypeId, currencyFormatter, priceRangeSeparator);

  return {
    screenZonesMap,
    zonePriceTypeMap,
    defaultPriceTypeId,
    priceRangeIncludesAllPriceTypes,
    zones,
  };
}

export default processPerformanceDetails;
